import type { FC } from 'react';
import { useCallback, useState, useEffect } from 'react';
import { lightColors } from '@tryhackme/thm-ui-components';
import { ThemeProvider, useTheme } from 'styled-components';
import { useLocation, Link } from 'react-router-dom';
import { store } from 'src/app/store';
import { setCurrentModal } from 'src/app/reducers/modal-states.reducer';
import { ModalType } from 'src/common/enums';
import { StyledCTAButtonLink } from 'src/common/components/cta-banner/cta-banner.styles';
import { useGetExamPurchaseDataQuery } from 'src/features/certifications/certification.slice';
import { CertificationType } from 'src/common/enums/certification';
import { Countdown } from 'src/features/campaigns/components/countdown';
import { CampaignName } from 'src/common/enums/campaigns';
import { useGetUserQuery } from 'src/common/slices';
import { checkMatch } from 'src/features/campaigns/helpers';
import { useInfluencerCampaign } from '../../hooks/use-influencer-campaign';
import { useInfluencerAdditionalConfigs } from '../../hooks/use-influencer-additional-configs';
import { InfluencerIcon } from '../influencer-icon/influencer-icon';
import { StyledBar, StyledCTAButtonLinkWrapper, StyledCopyAndIconWrapper } from './influencer-campaign-bar.styles';
interface InfluencerCampaignBarProps {
  influencerCampaign: CampaignName;
}
export const InfluencerCampaignBar: FC<InfluencerCampaignBarProps> = ({
  influencerCampaign
}) => {
  const [shouldShow, setShouldShow] = useState(false);
  const theme = useTheme();
  const {
    pathname
  } = useLocation();
  const {
    data: userData
  } = useGetUserQuery();
  const {
    data: purchaseData
  } = useGetExamPurchaseDataQuery({
    certificationType: CertificationType.PT1
  }, {
    skip: !userData?.data
  });
  const {
    campaign,
    isInInCurrentInfluencerSession,
    isExtended,
    shouldIncludeDays,
    userFriendlyName,
    percentageOff,
    calculatedEndTime
  } = useInfluencerCampaign(influencerCampaign);
  const additionalConfigs = useInfluencerAdditionalConfigs(campaign?.name);
  const {
    urlToNavigate,
    detailsRedirect,
    pagesToShowCTA,
    barCta,
    icon
  } = additionalConfigs ?? {};
  const shouldShowCTA = pagesToShowCTA ? checkMatch(pagesToShowCTA, pathname).isWideMatch || checkMatch(pagesToShowCTA, pathname).doesMatch : false;
  useEffect(() => {
    const show = !!isInInCurrentInfluencerSession && !!campaign;
    setShouldShow(show);
  }, [isInInCurrentInfluencerSession, campaign, purchaseData, pathname, userData?.data, pagesToShowCTA, detailsRedirect]);
  const handleWelcomeGetStarted = useCallback(() => {
    if (pathname === '/welcome') {
      const {
        dispatch
      } = store;
      dispatch(setCurrentModal(ModalType.INFLUENCER_CAMPAIGN));
    }
  }, [pathname]);
  if (!campaign) return null;
  if (!Object.values(CampaignName).includes(campaign.name)) {
    return null;
  }
  const navigateTo = userData?.data ? detailsRedirect : urlToNavigate;
  const startTime = campaign?.startTime ?? 0;
  const barCtaContent = barCta ? barCta(userFriendlyName, percentageOff, pathname === '/onboarding') : null;
  const isGradient = ['/onboarding', '/welcome'].includes(pathname);
  return shouldShow ? <ThemeProvider theme={{
    ...theme,
    colors: lightColors
  }}>
      <StyledBar isGradient={isGradient}>
        <StyledCopyAndIconWrapper>
          {icon && userFriendlyName && <InfluencerIcon icon={icon} alt={userFriendlyName} />}
          <p>{barCtaContent}</p>
        </StyledCopyAndIconWrapper>
        <StyledCTAButtonLinkWrapper>
          {shouldShowCTA && navigateTo ? <StyledCTAButtonLink as={Link} to={navigateTo} onClick={handleWelcomeGetStarted} variant="solid" color="primary">
              Get Started
            </StyledCTAButtonLink> : null}
          {!isExtended && <Countdown dark includeDays={shouldIncludeDays} startTime={startTime} endTime={calculatedEndTime} />}
        </StyledCTAButtonLinkWrapper>
      </StyledBar>
    </ThemeProvider> : null;
};